import { useSectionAccess } from "../../auth/useSectionAccess";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { intakeLotsApi, type IntakeLot } from "../../api/lots";
import { Registar, type Kolona } from "../../components/Registar";
import { formatDate, formatNumber } from "../../lib/format";

/**
 * The register of lots intake went into.
 *
 * A lot is what physically went into one silo, and it holds intakes from several co-operatives —
 * which is why it belongs to Bankom rather than to any one supplier.
 *
 * The rows come from the ERP intake notes, through `delivery.goods_lot_id`. Until that column
 * existed the screen read `receiving_lot`, a register the portal fills by hand and which stands
 * empty, so it said "no lots" while 280 lots and 22.132 intakes sat in the database.
 *
 * Every row carries its intake count on purpose. A lot is NOT a truck: it holds 79 intakes on
 * average and up to 1.099, over 147 days on average. A reader who takes one row for one delivery
 * reads the mass and the findings wrong, so the count sits next to both.
 */
export default function LotsPage() {
  const { canRead: canReadArchive } = useSectionAccess("lab_archive");
  const { t } = useTranslation();
  const [trazeno, setTrazeno] = useState("");
  const [upit, setUpit] = useState("");

  const kolone = useMemo<Kolona<IntakeLot>[]>(
    () => [
      {
        kljuc: "barcode",
        zaglavlje: t("lots.col.lot", "Šarža"),
        labela: t("lots.col.lot", "Šarža"),
        crtaj: (r) => <b>{r.barcode}</b>,
      },
      {
        kljuc: "productName",
        zaglavlje: t("lots.col.product", "Roba"),
        labela: t("lots.col.product", "Roba"),
        crtaj: (r) => r.productName ?? "—",
      },
      {
        kljuc: "intakeCount",
        zaglavlje: t("lots.col.intakes", "Prijema"),
        labela: t("lots.col.intakes", "Prijema"),
        broj: true,
        crtaj: (r) => formatNumber(r.intakeCount),
      },
      {
        kljuc: "netKg",
        zaglavlje: t("lots.col.netKg", "Neto (kg)"),
        labela: t("lots.col.netKg", "Neto (kg)"),
        broj: true,
        crtaj: (r) => formatNumber(r.netKg),
      },
      {
        kljuc: "period",
        zaglavlje: t("lots.col.period", "Period prijema"),
        labela: t("lots.col.period", "Period prijema"),
        crtaj: (r) =>
          r.firstIntake && r.lastIntake
            ? r.firstIntake === r.lastIntake
              ? formatDate(r.firstIntake)
              : `${formatDate(r.firstIntake)} — ${formatDate(r.lastIntake)}`
            : "—",
      },
      {
        kljuc: "analysisCount",
        zaglavlje: t("lots.col.analyses", "Nalaza"),
        labela: t("lots.col.analyses", "Nalaza"),
        broj: true,
        // Nula je tacan odgovor, ne podatak koji nedostaje: od 280 sarzi prijema 114 nosi nalaz.
        crtaj: (r) => (r.analysisCount != null ? formatNumber(r.analysisCount) : "—"),
      },
    ],
    [t],
  );

  return (
    <Registar<IntakeLot>
      naslov={t("lots.title", "Prijemne šarže")}
      podnaslov={t(
        "lots.subtitle",
        "Šta je fizički otišlo u jedan silos. Šarža može držati isporuke od više kooperanata.",
      )}
      filteri={
        <input
          className="input"
          type="search"
          value={trazeno}
          placeholder={t("lots.search", "Oznaka šarže ili roba")}
          onChange={(e) => setTrazeno(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setUpit(trazeno);
          }}
          onBlur={() => setUpit(trazeno)}
          style={{ minWidth: 240 }}
        />
      }
      kljucFiltera={upit}
      filtrirano={upit.trim() !== ""}
      ucitaj={(strana, velicina) => intakeLotsApi.page(strana, velicina, upit)}
      kolone={canReadArchive ? kolone : kolone.filter((column) => column.kljuc !== "analysisCount")}
      kljucReda={(r) => r.id}
      vezaNaRed={(r) => `/procurement/lots/${r.id}`}
      napomena={t(
        "lots.note",
        "Šarža nije kamion: drži prijeme više dana i više kooperanata, pa se nalaz na njoj odnosi na šaržu, a ne na pojedinačnu isporuku.",
      )}
      prazno={t("lots.empty", "Nema šarži.")}
      praznoUzFilter={t("lots.emptyFiltered", "Nijedna šarža ne odgovara pretrazi.")}
    />
  );
}
